// Customer portal — AI Assistant section.
//
// White-labelled chat assistant for the homeowner. Collapsed by default with
// a "Start chat" button to keep the dashboard clean; expands to reveal a
// chat panel + input.
//
// Source-of-truth (legacy):
//   /var/www/kitchenos/customer-live.js  ~line 815  (toggleFabChat / sendFabMsg)
//   /var/www/kitchenos-api/server.js     :521       (POST /api/customer-ai)
//
// Backend
// -------
//   POST /api/customer-ai   body: { message, history: [{role,content}] }
//                           response: { reply | message }
// The endpoint exists (server.js:521) and is rate-limited by `aiLimiter`.
// We include lightweight project context in the request body so the assistant
// can answer with specifics — the backend system prompt
// (CUSTOMER_AI_SYSTEM_PROMPT) is responsible for enforcing the safety rules
// described below, but we layer a small client-side guard for the worst-case
// "endpoint went away" scenario.
//
// Safety rules (per master doc Customer AI spec)
// ---------------------------------------------
//   1. Never reveal trade prices.   We deliberately strip trade-price fields
//      from the `projectContext` payload — we only forward retail totals.
//   2. Never promise dates the retailer hasn't confirmed.   The system prompt
//      handles this; we additionally include `installDate` only when set so
//      the model has nothing to invent.
//   3. If the AI can't help, suggest "I'll put you in touch with the retailer".
//      We post-process replies that contain the magic token `[[ESCALATE]]`
//      (or that match a confused-bot heuristic) and append an "Ask your
//      retailer" CTA that scrolls to the Messages section.
//
// Stub fallback
// -------------
// If `/api/customer-ai` returns 404 or 501, we treat the assistant as
// unavailable: a friendly canned message is shown and the input is disabled
// so the customer is steered toward the Messages section.

import { ApiError } from "@tkos/api-client";

import type {
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";

import "./ai-assistant.css";

// ── Types ───────────────────────────────────────────────────────────────────

interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  /** ms-since-epoch */
  ts: number;
}

interface AiResponse {
  ok?: boolean;
  reply?: string;
  message?: string;
  error?: string;
}

// ── Constants ───────────────────────────────────────────────────────────────

const HISTORY_WINDOW = 8;
const ESCALATE_TOKEN = "[[ESCALATE]]";
const FALLBACK_DECLINE = /\b(i (?:can'?t|don'?t|am unable)|sorry,? i)\b/i;

// ── Helpers ─────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** Build the safe, customer-visible project snapshot to send with each turn.
 *  Explicit allowlist — never include trade prices, internal status notes,
 *  or anything the homeowner shouldn't see. */
function safeContext(project: CustomerProject): Record<string, unknown> {
  const customer = project.customer;
  const quote = project.quote;
  return {
    customer: {
      firstName: customer.firstName,
      lastName: customer.lastName,
    },
    retailer: {
      businessName: project.retailer.businessName,
    },
    project: {
      status: project.status,
      name: project.projectName,
      // Only forward `installDate` when the retailer has actually set it.
      // Per spec #2 the AI must not invent dates.
      installDate: project.installDate,
    },
    // Only include RETAIL totals — never per-item trade prices.
    quote: quote
      ? {
          reference: quote.reference,
          status: quote.status,
          totalIncVat: quote.totalIncVat,
          deliveryCharge: quote.deliveryCharge,
          sentAt: quote.sentAt,
          approvedAt: quote.approvedAt,
        }
      : null,
    deliveries: project.deliveries.map((d) => ({
      kosReference: d.kosReference,
      status: d.status,
      eta: d.eta,
    })),
    paymentSchedule: project.paymentSchedule.map((s) => ({
      stageName: s.stageName,
      amount: s.amount,
      dueDate: s.dueDate,
      status: s.status,
    })),
  };
}

// ── Section ─────────────────────────────────────────────────────────────────

const section: CustomerSection = {
  id: "ai-assistant",
  label: "AI Assistant",

  shouldRender(_: CustomerProject): boolean {
    return true;
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const ac = new AbortController();
    let disposed = false;

    /** Most-recent in-flight send so we can abort it on cleanup / new send. */
    let inFlight: AbortController | null = null;

    /** "true" once we've confirmed the backend is reachable / unavailable. */
    let backendDisabled = false;
    let busy = false;
    let opened = false;

    const history: ChatTurn[] = [];

    const retailer = ctx.project.retailer;
    const retailerName = retailer.businessName || "your designer";
    const welcome = `Hi! I'm ${esc(retailerName)}'s AI assistant. I know your project inside-out — ask me about your timeline, quote, deliveries, or anything kitchen-related.`;

    // ── Initial chrome ───────────────────────────────────────────────────
    container.innerHTML = `
      <section class="cust-card ai-card" aria-label="AI Assistant">
        <h2 class="cust-card-title">Ask ${esc(retailerName)}'s AI</h2>
        <p class="ai-help">I know your project inside-out. Ask me anything about your kitchen, timeline, or quote.</p>

        <div class="ai-collapsed" data-role="collapsed">
          <button type="button" class="cust-btn ai-start" data-role="start">Start chat</button>
          <span class="ai-collapsed__hint">Or send a question directly to your designer via Messages above.</span>
        </div>

        <div class="ai-expanded" data-role="expanded" hidden>
          <div class="ai-body" data-role="body" aria-live="polite" aria-busy="false"></div>
          <form class="ai-foot" data-role="form" novalidate>
            <textarea class="ai-input"
                      data-role="input"
                      rows="1"
                      maxlength="2000"
                      placeholder="Ask a question…"
                      aria-label="Ask the AI assistant"></textarea>
            <div class="ai-foot__actions">
              <button type="button" class="cust-btn cust-btn-ghost ai-close" data-role="close">Close</button>
              <button type="submit" class="cust-btn ai-send" data-role="send"
                      aria-label="Send to AI assistant">Send</button>
            </div>
          </form>
        </div>
      </section>
    `;

    const collapsedEl = container.querySelector<HTMLElement>("[data-role='collapsed']")!;
    const expandedEl = container.querySelector<HTMLElement>("[data-role='expanded']")!;
    const startBtn = container.querySelector<HTMLButtonElement>("[data-role='start']")!;
    const bodyEl = container.querySelector<HTMLElement>("[data-role='body']")!;
    const formEl = container.querySelector<HTMLFormElement>("[data-role='form']")!;
    const inputEl = container.querySelector<HTMLTextAreaElement>("[data-role='input']")!;
    const sendBtn = container.querySelector<HTMLButtonElement>("[data-role='send']")!;
    const closeBtn = container.querySelector<HTMLButtonElement>("[data-role='close']")!;

    // ── Rendering ────────────────────────────────────────────────────────
    function turnHtml(t: ChatTurn, opts: { escalate?: boolean } = {}): string {
      const side = t.role === "user" ? "user" : "bot";
      const escalateBtn = opts.escalate
        ? `<button type="button" class="ai-escalate" data-action="escalate">
             Ask your designer at ${esc(retailerName)} →
           </button>`
        : "";
      return `
        <article class="ai-bubble ai-bubble--${side}">
          <div class="ai-bubble__text">${esc(t.content).replace(/\n/g, "<br>")}</div>
          ${escalateBtn}
        </article>
      `;
    }

    function thinkingHtml(): string {
      return `
        <article class="ai-bubble ai-bubble--bot ai-bubble--thinking" data-role="thinking">
          <span class="ai-dots" aria-hidden="true"><span></span><span></span><span></span></span>
          <span class="visually-hidden">Thinking…</span>
        </article>
      `;
    }

    function shouldEscalate(reply: string): boolean {
      if (!reply) return true;
      if (reply.includes(ESCALATE_TOKEN)) return true;
      // Heuristic: a short refusal like "Sorry, I can't help with that" → CTA.
      if (reply.length < 220 && FALLBACK_DECLINE.test(reply)) return true;
      return false;
    }

    function renderHistory(thinking = false): void {
      const items: string[] = [];
      items.push(turnHtml({ role: "assistant", content: welcome, ts: 0 }));
      for (const t of history) {
        const escalate = t.role === "assistant" && shouldEscalate(t.content);
        items.push(turnHtml(t, { escalate }));
      }
      if (thinking) items.push(thinkingHtml());
      bodyEl.innerHTML = items.join("");
      bodyEl.scrollTop = bodyEl.scrollHeight;
    }

    function setBusy(b: boolean): void {
      busy = b;
      sendBtn.disabled = b || backendDisabled;
      bodyEl.setAttribute("aria-busy", b ? "true" : "false");
    }

    function disableBackend(reason: string): void {
      backendDisabled = true;
      const note: ChatTurn = {
        role: "assistant",
        content: reason,
        ts: Date.now(),
      };
      history.push(note);
      renderHistory(false);
      inputEl.disabled = true;
      sendBtn.disabled = true;
      inputEl.placeholder = "AI is unavailable — use Messages above";
    }

    // ── Open / close ─────────────────────────────────────────────────────
    function openChat(): void {
      if (opened) return;
      opened = true;
      collapsedEl.hidden = true;
      expandedEl.hidden = false;
      renderHistory();
      setTimeout(() => inputEl.focus(), 0);
    }

    function closeChat(): void {
      if (!opened) return;
      opened = false;
      collapsedEl.hidden = false;
      expandedEl.hidden = true;
      // Abort in-flight request so a delayed reply doesn't pop in while
      // the panel is collapsed.
      if (inFlight) {
        inFlight.abort();
        inFlight = null;
      }
      setBusy(false);
    }

    // ── Backend ──────────────────────────────────────────────────────────
    async function ask(question: string): Promise<void> {
      const trimmed = question.trim();
      if (!trimmed || busy || backendDisabled) return;

      history.push({ role: "user", content: trimmed, ts: Date.now() });
      renderHistory(true);
      setBusy(true);

      const local = new AbortController();
      inFlight = local;
      // Cancel the local controller if the section is torn down mid-request.
      const onAbort = (): void => local.abort();
      ac.signal.addEventListener("abort", onAbort, { once: true });
      const signal = local.signal;

      try {
        const payload = {
          message: trimmed,
          history: history.slice(-HISTORY_WINDOW).map((t) => ({
            role: t.role,
            content: t.content,
          })),
          context: safeContext(ctx.project),
        };
        const resp = await ctx.api<AiResponse>("/customer-ai", {
          method: "POST",
          signal,
          body: JSON.stringify(payload),
        });
        if (disposed || signal.aborted) return;
        const reply =
          (resp.reply ?? resp.message ?? "").toString().trim() ||
          "I'm not sure how to help with that. Try asking your designer via Messages above.";
        history.push({ role: "assistant", content: reply, ts: Date.now() });
        renderHistory(false);
      } catch (err) {
        if (isAbortError(err) || signal.aborted) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          // Backend not deployed — switch into stub mode.
          disableBackend(
            `I'm still learning! For now, please use the chat above to message ${retailerName} directly.`,
          );
          return;
        }
        const msg =
          err instanceof ApiError
            ? `Sorry, the assistant is having a moment (HTTP ${err.status}). Please try again, or message ${retailerName} directly.`
            : `Sorry, I couldn't reach the assistant. Please try again, or message ${retailerName} directly.`;
        history.push({ role: "assistant", content: msg, ts: Date.now() });
        renderHistory(false);
        ctx.showToast("AI assistant unavailable", "error");
      } finally {
        ac.signal.removeEventListener("abort", onAbort);
        if (inFlight === local) inFlight = null;
        if (!disposed) setBusy(false);
      }
    }

    // ── Events ───────────────────────────────────────────────────────────
    function onStart(): void {
      openChat();
    }
    startBtn.addEventListener("click", onStart);

    function onClose(): void {
      closeChat();
    }
    closeBtn.addEventListener("click", onClose);

    function onSubmit(e: SubmitEvent): void {
      e.preventDefault();
      const v = inputEl.value;
      if (!v.trim() || busy || backendDisabled) return;
      inputEl.value = "";
      autoSizeInput();
      void ask(v);
    }
    formEl.addEventListener("submit", onSubmit);

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const v = inputEl.value;
        if (!v.trim() || busy || backendDisabled) return;
        inputEl.value = "";
        autoSizeInput();
        void ask(v);
      }
    }
    inputEl.addEventListener("keydown", onKeyDown);

    function autoSizeInput(): void {
      inputEl.style.height = "auto";
      const next = Math.min(inputEl.scrollHeight, 120);
      inputEl.style.height = `${next}px`;
    }
    inputEl.addEventListener("input", autoSizeInput);

    function onBodyClick(e: MouseEvent): void {
      const action = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        "[data-action='escalate']",
      );
      if (!action) return;
      e.preventDefault();
      // Scroll to the Messages section.
      const msgSection = document.querySelector<HTMLElement>(
        "[data-section-id='messages'], [aria-label='Messages']",
      );
      if (msgSection) {
        msgSection.scrollIntoView({ behavior: "smooth", block: "start" });
        const input = msgSection.querySelector<HTMLTextAreaElement>(".msg-input");
        if (input) setTimeout(() => input.focus(), 350);
      } else {
        ctx.showToast(`Scroll up to the Messages section to message ${retailerName}.`, "info");
      }
    }
    bodyEl.addEventListener("click", onBodyClick);

    // ── Cleanup ──────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      if (inFlight) inFlight.abort();
      ac.abort();
      startBtn.removeEventListener("click", onStart);
      closeBtn.removeEventListener("click", onClose);
      formEl.removeEventListener("submit", onSubmit);
      inputEl.removeEventListener("keydown", onKeyDown);
      inputEl.removeEventListener("input", autoSizeInput);
      bodyEl.removeEventListener("click", onBodyClick);
    };
  },
};

export default section;
